import {Component, Inject, Input, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material';
import {BehaviorSubject} from '@node_modules/rxjs';
import {Program} from '@models/program';
import {MembersInterface} from '@interfaces/members.interface';
import {AttendanceService} from '@services/attendance/attendance.service';
import {Attendance} from '@models/attendance';
import {Lesson} from '@models/lesson';
import {UpdateAuthUser} from '@store/auth/actions';
import {AccountService} from '@services/account/account.service';
import {User} from '@models/user';
import * as moment from 'moment';
import {ConfirmModalComponent} from '@components/confirm-modal/confirm-modal.component';
import {Store} from '@node_modules/@ngrx/store';
import * as AuthReducer from '@store/auth/reducers';
import {Class} from '@models/class';
import {ProgramService} from '@services/program/program.service';
import {Member} from '@models/member';

@Component({
  selector: 'app-attendance-teacher',
  templateUrl: './attendance-teacher.component.html',
  styleUrls: ['./attendance-teacher.component.scss']
})
export class AttendanceTeacherComponent implements OnInit {
  static modalRef;
  @Input() programsSubject: BehaviorSubject<Program[]>;
  @Input() direct: BehaviorSubject<string>;
  /**
   *  'owner' unlocks the program selector and bulk Check In All/Uncheck All
   *  controls. Defaults to 'teacher' so existing Teacher dialogConfig.data
   *  (which never sets `mode`) is completely unaffected.
   */
  mode: 'teacher' | 'owner' = 'teacher';
  programs: Program[] = [];
  selectedProgramId: number;
  programHeader: string;
  classes;
  users = [];
  // selectedClass;
  selectedClassSchedule;
  // selectedClassId;
  selectedClassScheduleId;
  programId: number;
  teachersAttendances = [];
  studentsAttendances = [];
  studentsAttendancesId = [];
  schedules = [];
  allAttendances = [];
  searchResults: BehaviorSubject<Member[]> = new BehaviorSubject(null);
  peopleDirectSearch: BehaviorSubject<string> = new BehaviorSubject('all');
  currentDirection = 'all';
  startSearching = false;
  confirmDialog;
  classToCheckinId: number;
  classesToCheckin: Class[] = [];
  user: User;
  backUpStudents = null;
  attendancesByUserId = {};
  bulkActionInProgress = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private attendanceService: AttendanceService,
    private accountService: AccountService,
    private programService: ProgramService,
    private authStore: Store<AuthReducer.AuthState>,
    public dialog: MatDialog,
  ) {
  }

  ngOnInit() {
    this.mode = this.data.mode === 'owner' ? 'owner' : 'teacher';
    this.user = this.data.user;

    if (this.mode === 'owner') {
      this.programs = this.data.programs || [];
      if (this.data.selectedProgramId !== undefined && this.data.selectedProgramId !== null) {
        this.selectedProgramId = this.data.selectedProgramId;
      } else if (this.programs.length) {
        this.selectedProgramId = this.programs[0].id !== undefined ? this.programs[0].id : this.programs[0].program_id;
      }
      this.rebuildOwnerClassesForSelectedProgram();
    } else {
      this.programId = this.data.selectedProgramId;
      this.programHeader = this.data.selectedProgramName;
      this.classes = this.data.classes;
      this.classesToCheckin = this.data.classesToCheckin;
      const weekDay = this.getDay();
      this.classes.map(curClass => {
        curClass.schedule.map(schedule => {
          if (weekDay === schedule.fullDayName) {
            schedule.class_name = curClass.name;
            schedule.class_id = curClass.id;
            this.schedules.push(schedule);
          }
        });
      });
      this.selectedClassSchedule = this.schedules[0];
      this.selectedClassScheduleId = this.schedules.length > 0 ? this.schedules[0].id : null;
    }

    this.setStudentProgramDirectSearch('all');
    // this.getAttendance();
  }

  /**
   *  Owner mode only: rebuild the class/schedule list from the currently
   *  selected active-school program.
   *
   *  DashboardAdminComponent.openAttendance() already resolves each
   *  program's classes down to today's occurrence before this component
   *  ever receives them (using the exact same day-matching logic as the
   *  Owner Dashboard's own `todaySchedules` getter). This method therefore
   *  does not re-filter by day of week — `curClass.schedule` here already
   *  IS today's occurrence. Re-deriving "is this today" here with a second,
   *  incompatible check (comparing against `fullDayName`, which real
   *  backend schedules never populate) was the cause of Iteration 1033's
   *  "There is no classes today" bug despite the Dashboard showing a real
   *  current-day class.
   */
  rebuildOwnerClassesForSelectedProgram() {
    this.schedules = [];
    const program = this.programs.find(p =>
      (p.id !== undefined && p.id === this.selectedProgramId) || p.program_id === this.selectedProgramId
    );
    this.programHeader = program ? program.program_name : '';
    this.programId = this.selectedProgramId;
    this.classes = program ? (program.classes || []) : [];
    this.classes.map(curClass => {
      (curClass.schedule || []).map(schedule => {
        schedule.class_name = schedule.class_name || curClass.name;
        schedule.class_id = schedule.class_id || curClass.id;
        schedule.teachers = curClass.teachers || [];
        this.schedules.push(schedule);
      });
    });
    this.selectedClassSchedule = this.schedules[0];
    this.selectedClassScheduleId = this.schedules.length > 0 ? this.schedules[0].id : null;
    this.getAttendance();
  }

  selectProgram(event) {
    this.selectedProgramId = event.value;
    this.rebuildOwnerClassesForSelectedProgram();
  }

  getDay() {
    const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const mydate = new Date();
    return weekday[mydate.getDay()];
  }

  /**
   *  Shared by both modes since Iteration 1041: search must never add a
   *  Teacher, Owner/Admin, or any non-Student-role result into the Students
   *  list — only recognised Student roles not already present as a Teacher
   *  participant, merged with real attendance state exactly like the
   *  initial roster.
   */
  setSearchResults(results: { result: Array<Member>, query: string }) {
    if (results.query === '') {
      this.backUpStudents = null;
      this.getAttendance();
    } else {
      const membersIds = [];
      this.studentsAttendances.map(s => membersIds.push(s.user_id));
      const teacherIds = this.teachersAttendances.map(teacher => teacher.id);
      results.result.map((i: any) => {
        const isStudentRole = i.role === 'student' || i.role === 'student_individual' || i.role === 'student_group';
        if (!isStudentRole || teacherIds.includes(i.id)) {
          return;
        }
        if (!membersIds.includes(i.id)) {
          membersIds.push(i.id);
          i.student_name = i.name;
          i.student_avatar = i.avatar;
          i.user_id = i.id;
          const attendanceId = this.attendancesByUserId[i.id] || null;
          i.attendanceId = attendanceId;
          i.checkedIn = attendanceId ? 1 : 0;
          this.studentsAttendances.push(i);
        }
      });
      if (!this.backUpStudents) {
        this.backUpStudents = this.studentsAttendances;
      }
      this.startSearching = false;
      // this.studentsAttendances = this.backUpStudents.filter(i => membersIds.includes(i.id));
    }
    // if ((results.query && results.query.length === 0 && this.currentDirection !== 'all') ||
    //   results.result && results.result.length > 0 || this.startSearching === false) {
    //   this.searchResults.next(results.result);
    // } else {
    //   this.searchResults.next([]);
    // }
  }

  /**
   *  Shared by both modes since Iteration 1041: selecting a search result
   *  never adds a Teacher — Teachers come only from the assigned/authenticated
   *  Teacher source (`getOwnerAttendance()`/`getTeacherAttendance()`), never
   *  from search. De-duplicates and merges attendance state by real
   *  `user_id`, matching the normalized Student shape used everywhere else.
   */
  selectedPerson(value) {
    for (let i = 0; i < this.allAttendances.length; i++) {
      if (this.allAttendances[i].id === value.id) {
        return;
      }
    }
    this.allAttendances.push(value);
    const isStudentRole = value.role === 'student' || value.role === 'student_individual' || value.role === 'student_group';
    const teacherIds = this.teachersAttendances.map(teacher => teacher.id);
    if (!isStudentRole || teacherIds.includes(value.id)) {
      return;
    }
    const alreadyListed = this.studentsAttendances.some(s => s.user_id === value.id);
    if (alreadyListed) {
      return;
    }
    const attendanceId = this.attendancesByUserId[value.id] || null;
    this.studentsAttendances.push({
      user_id: value.id,
      student_name: value.name,
      student_avatar: value.avatar,
      role: value.role,
      checkedIn: attendanceId ? 1 : 0,
      attendanceId: attendanceId,
    });
  }

  setStudentProgramDirectSearch(direct: string) {
    this.startSearching = true;
    this.currentDirection = direct;
    this.peopleDirectSearch.next(direct);
  }

  selectClassSchedule(event) {
    this.schedules.map(schedule => {
      if (schedule.id === event.value) {
        this.selectedClassSchedule = Object.assign({}, schedule);
      }
    });
    this.getAttendance();
  }

  getAttendance() {
    if (this.mode === 'owner') {
      this.getOwnerAttendance();
    } else {
      this.getTeacherAttendance();
    }
  }

  /**
   *  Teacher mode. Canonical participant pipeline (Iteration 1041, mirroring
   *  Owner mode's Iteration 1037 repair):
   *
   *  1. The Teacher participant row is still the existing authenticated
   *     Teacher source (`this.user`) — unchanged; Teacher mode has never
   *     listed every assigned Teacher of the class, only the logged-in
   *     Teacher themself, and this iteration does not broaden that.
   *  2. Students come only from the role-filtered enrolled-student endpoint
   *     (`ProgramService.getStudents()` / backend `getStudentsWithActiveSubscription`,
   *     corrected in Iteration 1035) via the same shared roster builder
   *     Owner mode uses — called directly, bypassing the dead
   *     `combineLatest([])`/`filterStudents()` chain this method previously
   *     carried (that chain never ran: `subscribesAccounts` was always `[]`,
   *     so `combineLatest([])` completed without emitting, and the enrolled
   *     Student fetch that depended on it never executed — the same defect
   *     Iteration 1036 found and fixed for Owner mode, now fixed here too).
   *  3. Attendance records only ever enrich the already-built participants
   *     with `checkedIn`/`attendanceId`, never build or replace the roster,
   *     and never overwrite a participant's real id.
   */
  getTeacherAttendance() {
    this.startSearching = true;
    this.allAttendances = [];
    this.attendancesByUserId = {};

    this.teachersAttendances = [this.user];
    this.teachersAttendances[0].checkedIn = 0;
    this.teachersAttendances[0].attendanceId = null;

    const classId = this.selectedClassSchedule ? this.selectedClassSchedule.class_id : null;

    this.programService.getStudents(this.programId).subscribe(
      (resp: { data: any[] }) => {
        this.studentsAttendances = this.buildEligibleStudentRoster(resp.data, [this.user.id]);
        this.mergeAttendanceState(classId);
      },
      () => {
        this.studentsAttendances = [];
        this.mergeAttendanceState(classId);
      }
    );
  }

  /**
   *  Owner mode. Canonical participant pipeline (Iteration 1037):
   *
   *  1. Teachers come only from the selected class's assigned-teacher
   *     collection (`selectedClassSchedule.teachers`, resolved once by
   *     DashboardAdminComponent.openAttendance() from class_users — never
   *     touched by this method).
   *  2. Students come only from the role-filtered enrolled-student endpoint,
   *     via the shared roster builder (`buildEligibleStudentRoster`).
   *  3. Both collections are normalized into one stable shape up front and
   *     de-duplicated by real user id. Any id already present in the
   *     Teacher list is excluded from the Student list as a final
   *     invariant, so the same person can never render in both sections.
   *  4. Attendance records for the selected schedule/class are fetched
   *     last and only ever used to set `checkedIn`/`attendanceId` on the
   *     already-built participants, keyed by the attendance record's own
   *     `user_id` — never used to build or replace the participant arrays,
   *     and never spread onto a participant (so a participant's real id
   *     can never be overwritten by an attendance row's own id).
   */
  getOwnerAttendance() {
    this.startSearching = true;
    this.allAttendances = [];
    this.attendancesByUserId = {};

    const classTeachers = (this.selectedClassSchedule && this.selectedClassSchedule.teachers) || [];
    const teacherIds = [];
    this.teachersAttendances = [];
    classTeachers.forEach(teacher => {
      if (teacher && teacher.id !== undefined && !teacherIds.includes(teacher.id)) {
        teacherIds.push(teacher.id);
        this.teachersAttendances.push({
          id: teacher.id,
          name: teacher.name,
          avatar: teacher.avatar,
          role: 'teacher',
          checkedIn: 0,
          attendanceId: null,
        });
      }
    });

    const classId = this.selectedClassSchedule ? this.selectedClassSchedule.class_id : null;

    this.programService.getStudents(this.programId).subscribe(
      (resp: { data: any[] }) => {
        this.studentsAttendances = this.buildEligibleStudentRoster(resp.data, teacherIds);
        this.mergeAttendanceState(classId);
      },
      () => {
        this.studentsAttendances = [];
        this.mergeAttendanceState(classId);
      }
    );
  }

  /**
   *  Shared by both modes: normalizes a raw enrolled-student endpoint
   *  response into this component's stable participant shape
   *  (`user_id`/`student_name`/`student_avatar`), keeping only recognised
   *  Student roles, excluding any id already representing a Teacher
   *  participant (`excludeIds`), and de-duplicating by real user id.
   *  `checkedIn`/`attendanceId` start unset here — `mergeAttendanceState()`
   *  is the only place that ever sets them, from real attendance records.
   */
  private buildEligibleStudentRoster(students: any[], excludeIds: number[]): any[] {
    const studentIds = [];
    const roster = [];
    (students || []).forEach(student => {
      const isStudentRole = student.role === 'student'
        || student.role === 'student_individual'
        || student.role === 'student_group';
      if (!isStudentRole) {
        return;
      }
      if (excludeIds.includes(student.id) || studentIds.includes(student.id)) {
        return;
      }
      studentIds.push(student.id);
      roster.push({
        user_id: student.id,
        student_name: student.name,
        student_avatar: student.avatar,
        role: student.role,
        checkedIn: 0,
        attendanceId: null,
      });
    });
    return roster;
  }

  /**
   *  Shared by both modes: fetches attendance records for the selected
   *  schedule/class and merges `checkedIn`/`attendanceId` onto the already
   *  built `teachersAttendances`/`studentsAttendances`, keyed by each
   *  attendance record's own `user_id`. Never builds or replaces either
   *  array, and never spreads a raw attendance record onto a participant.
   */
  private mergeAttendanceState(classId: any) {
    this.attendanceService.getAllAttendences({
      class_id: classId,
      schedule_id: this.selectedClassScheduleId
    }).subscribe(
      (resp1: { data: any[] }) => {
        const attendanceByUserId = {};
        this.attendancesByUserId = {};
        (resp1.data || []).forEach(attendance => {
          attendanceByUserId[attendance.user_id] = attendance;
          this.attendancesByUserId[attendance.user_id] = attendance.id;
        });
        this.teachersAttendances.forEach(teacher => {
          const attendance = attendanceByUserId[teacher.id];
          teacher.checkedIn = attendance ? 1 : 0;
          teacher.attendanceId = attendance ? attendance.id : null;
        });
        this.studentsAttendances.forEach(student => {
          const attendance = attendanceByUserId[student.user_id];
          student.checkedIn = attendance ? 1 : 0;
          student.attendanceId = attendance ? attendance.id : null;
        });
        this.startSearching = false;
      },
      () => {
        this.startSearching = false;
      }
    );
  }

  filterStudents(resp) {
    this.studentsAttendances = [];
    const uniqIds = [];
    resp.data.forEach(student => {
      // Defensive role guard matching the same field/values already checked
      // above in getAttendance() (line ~272) — the backend endpoint this
      // feeds from is now role-filtered server-side too, but this keeps the
      // Students list correct even if a non-student user record ever slips
      // through (e.g. stray subscription data), rather than trusting the
      // response shape blindly.
      const isStudentRole = student.role === 'student'
        || student.role === 'student_individual'
        || student.role === 'student_group';
      if (!isStudentRole) {
        return;
      }
      if (this.studentsAttendancesId.includes(student.id)) {
        student.checkedIn = 1;
      } else {
        student.checkedIn = 0;
      }
      if (!uniqIds.includes(student.id)) {
        uniqIds.push(student.id);
        this.studentsAttendances.push(student);
      }
    });
  }

  checkin() {
    const body = {
      user_id: this.users,
      program_id: this.programId,
      schedule_id: this.selectedClassSchedule.id,
      class_id: this.selectedClassSchedule.class_id,
      lesson_id: this.selectedClassSchedule.lesson ? this.selectedClassSchedule.lesson.id : null,
      created_at: moment().format('YYYY-MM-DD HH:mm:ss')
    };
    this.attendanceService.checkIn(body).subscribe(
      (resp: { data: any, status: number }) => {
        for (let i = 0; i < resp.data.length; i++) {
          const data = resp.data[i];
          if (data.status === 11200) {
            ConfirmModalComponent.insertedObject = this;
            ConfirmModalComponent.methodDelete = 'close';
            ConfirmModalComponent.textModal = data.error;
            ConfirmModalComponent.textButton = 'OK';
            ConfirmModalComponent.hideCancel = true;
            this.confirmDialog = this.dialog.open(ConfirmModalComponent);
            ConfirmModalComponent.modalRef = this.confirmDialog;
            break;
          }
        }
        this.close();
      },
      err => {
        console.log(err);
        console.log(err.error);
        ConfirmModalComponent.insertedObject = this;
        ConfirmModalComponent.withButtons = false;
        ConfirmModalComponent.textModal = err.error.message;
        this.confirmDialog = this.dialog.open(ConfirmModalComponent);
        ConfirmModalComponent.modalRef = this.confirmDialog;
      });
  }

  onChange(event, id) {
    if (event.checked) {
      this.users.push(id);
    } else {
      const index = this.users.indexOf(id);
      this.users.splice(index, 1);
    }
  }
  /**
   *  Shared by both modes since Iteration 1041: every participant (Teacher
   *  or Student, either mode) now has a real user id on `id`/`user_id` and a
   *  separate `attendanceId` set only by `mergeAttendanceState()`, so
   *  identity can never be ambiguous or corrupted here.
   */
  onTryToUnCheckIn(participant) {
    if (!participant.attendanceId) {
      return;
    }
    const userId = participant.user_id !== undefined ? participant.user_id : participant.id;
    const displayName = participant.student_name || participant.name;
    ConfirmModalComponent.insertedObject = this;
    ConfirmModalComponent.textModal = `Uncheck ${displayName}?`;
    ConfirmModalComponent.textButton = 'Un Check In';
    ConfirmModalComponent.methodDelete = 'unCheckIn';
    ConfirmModalComponent.methodDeleteParam = {attendance_id: participant.attendanceId, student_id: userId};
    this.confirmDialog = this.dialog.open(ConfirmModalComponent);
    ConfirmModalComponent.modalRef = this.confirmDialog;
  }
  unCheckIn(body: {attendance_id: number, student_id: number}) {
    this.attendanceService.unCheckIn({attendance_id: [body.attendance_id]}).subscribe(resp => {
      this.getAttendance();
    });
  }

  /**
   *  Owner mode only: everyone in the selected class/schedule occurrence who
   *  is not yet checked in.
   */
  get uncheckedEligiblePeople(): any[] {
    return [...this.teachersAttendances, ...this.studentsAttendances].filter(person => !person.checkedIn);
  }

  /**
   *  Owner mode only: everyone in the selected class/schedule occurrence who
   *  is already checked in.
   */
  get checkedInPeople(): any[] {
    return [...this.teachersAttendances, ...this.studentsAttendances].filter(person => !!person.checkedIn);
  }

  /**
   *  Owner mode only. Reuses the exact same `checkin()` path a Teacher uses
   *  for an individually-selected person, just with every currently eligible
   *  unchecked person in the selected class/schedule pre-selected.
   */
  checkInAll() {
    if (this.bulkActionInProgress) {
      return;
    }
    const targets = this.uncheckedEligiblePeople;
    if (!targets.length) {
      return;
    }
    const ids = [];
    targets.forEach(person => {
      const id = person.id !== undefined ? person.id : person.user_id;
      if (id !== undefined && !ids.includes(id)) {
        ids.push(id);
      }
    });
    if (!ids.length) {
      return;
    }
    this.users = ids;
    this.bulkActionInProgress = true;
    this.checkin();
  }

  /**
   *  Owner mode only. Reuses the existing ConfirmModalComponent confirmation
   *  pattern already applied to individual uncheck (`onTryToUnCheckIn`).
   */
  uncheckAll() {
    if (this.bulkActionInProgress) {
      return;
    }
    const targets = this.checkedInPeople;
    if (!targets.length) {
      return;
    }
    const attendanceIds = [];
    targets.forEach(person => {
      if (person.attendanceId && !attendanceIds.includes(person.attendanceId)) {
        attendanceIds.push(person.attendanceId);
      }
    });
    if (!attendanceIds.length) {
      return;
    }
    ConfirmModalComponent.insertedObject = this;
    ConfirmModalComponent.textModal = `Uncheck all ${attendanceIds.length} checked-in people?`;
    ConfirmModalComponent.textButton = 'Uncheck All';
    ConfirmModalComponent.methodDelete = 'unCheckInBulk';
    ConfirmModalComponent.methodDeleteParam = {attendance_ids: attendanceIds};
    this.confirmDialog = this.dialog.open(ConfirmModalComponent);
    ConfirmModalComponent.modalRef = this.confirmDialog;
  }

  unCheckInBulk(body: {attendance_ids: number[]}) {
    this.bulkActionInProgress = true;
    this.attendanceService.unCheckIn({attendance_id: body.attendance_ids}).subscribe(
      () => {
        this.bulkActionInProgress = false;
        this.getAttendance();
      },
      () => {
        this.bulkActionInProgress = false;
      }
    );
  }

  onChangeClass(event) {
    console.log('here');
    console.log(event);
  }

  /**
   *  Modal-scoped Banani fallback (Iteration 1043): a participant has a real
   *  photo only if their own avatar field is a non-empty string AND its image
   *  has not already failed to load this session. Both Teacher (`avatar`) and
   *  Student (`student_avatar`) rows call this with their own field's value,
   *  so no shared avatar-field naming is required.
   */
  hasRealAvatar(participant: any, avatar: string): boolean {
    return !!(avatar && avatar.length > 0) && !(participant && participant.avatarLoadFailed);
  }

  /**
   *  Modal-scoped Banani fallback (Iteration 1043): flips just this one
   *  row's own view-model object to the fallback state so a dead image URL
   *  can never keep re-requesting or show a broken-image icon. This mutates
   *  only the local `teachersAttendances`/`studentsAttendances` list item
   *  built by this component, never the underlying user record.
   */
  onAvatarError(participant: any) {
    if (participant) {
      participant.avatarLoadFailed = true;
    }
  }

  close() {
    AttendanceTeacherComponent.modalRef.close();
  }
}
